/**
 * 現在の pathname に対して最長一致する menu を `activeMenuName` として返す。
 * クエリ / ハッシュは無視する (例: neuro の `${basePath}?id=<agentId>`)。
 */

import { useSyncExternalStore } from 'react';
import type { MenuGroup, MenuItem, ProductMenu } from '../types/menuModel.js';

const subscribePathname = (callback: () => void) => {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener('popstate', callback);
  window.addEventListener('hashchange', callback);
  return () => {
    window.removeEventListener('popstate', callback);
    window.removeEventListener('hashchange', callback);
  };
};
const getPathnameSnapshot = () => (typeof window === 'undefined' ? '' : window.location.pathname);
const getServerPathname = () => '';

export const usePathname = (): string => useSyncExternalStore(subscribePathname, getPathnameSnapshot, getServerPathname);

const normalizePath = (path: string) => {
  const withoutQuery = path.split('?')[0].split('#')[0];
  return withoutQuery !== '/' && withoutQuery.endsWith('/') ? withoutQuery.slice(0, -1) : withoutQuery;
};

const isMenuPathActive = (menuPath: string, currentPath: string): boolean => {
  const m = normalizePath(menuPath);
  const c = normalizePath(currentPath);
  return c === m || c.startsWith(`${m}/`);
};

export const findActiveMenuName = (product: ProductMenu, pathname: string): string | undefined => {
  // popover 内の item も active 判定の対象に含める (trigger 側は item name の一致で点灯する)
  const allMenus = product.menuGroups.flatMap((g: MenuGroup) =>
    g.menus.flatMap((m: MenuItem) => [m, ...(m.popoverMenus?.flatMap((pg) => pg.items) ?? [])]),
  );
  const candidates = allMenus.filter((m: MenuItem): m is MenuItem & { path: NonNullable<MenuItem['path']> } => !!m.path?.path);
  // 最長一致 (より具体的なパス) を優先
  const sorted = [...candidates].sort((a, b) => b.path.path.length - a.path.path.length);
  const hit = sorted.find((m) => isMenuPathActive(m.path.path, pathname));
  return hit?.name;
};
